/**
 * Recover transparently from stale-deploy chunk failures.
 *
 * After a redeploy, code-split asset filenames get fresh content hashes and
 * the previous ones are deleted from the server. A tab still running the old
 * build references the old hashes, so the first lazy import (opening a
 * .docx/.xlsx/.pdf view, the PDF-export libs, …) 404s with "Failed to fetch
 * dynamically imported module" and Vite fires `vite:preloadError`.
 *
 * We reload once to pull the fresh index.html + asset map; the user re-does
 * the action against the new build and never sees the error. A short
 * sessionStorage cooldown prevents an infinite reload loop when a chunk is
 * genuinely broken in the *current* build — in that case we let Vite throw so
 * the failure surfaces to the nearest error boundary instead of reloading.
 *
 * Relies on `index.html` being served with `Cache-Control: no-cache` (see
 * nginx.conf): otherwise the reload re-serves a cached entry pointing at the
 * same dead hashes and the cooldown just turns the loop into a hard error.
 */
const COOLDOWN_KEY = "chunk-reload-at";
const COOLDOWN_MS = 10_000;

export function installChunkReloadGuard(): void {
  window.addEventListener("vite:preloadError", (event) => {
    const last = Number(sessionStorage.getItem(COOLDOWN_KEY) ?? 0);
    // Reloaded moments ago and still failing → not staleness. Let it throw.
    if (Date.now() - last < COOLDOWN_MS) return;
    event.preventDefault(); // we own the recovery; stop Vite from rethrowing
    sessionStorage.setItem(COOLDOWN_KEY, String(Date.now()));
    window.location.reload();
  });
}
